import { DeleteOutlined, ReloadOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import type { ApplyRow, FavoriteRow, UrgentLevel } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatQty } from '@hq/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Empty, Form, Input, InputNumber, Modal, Radio, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hq } from '../api/client';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';
import { useAuthStore } from '../stores/auth';

interface PurchaseDraft {
  title: string;
  urgentLevel: UrgentLevel;
}

const DEFAULT_DRAFT: PurchaseDraft = { title: '', urgentLevel: 'NORMAL' };

/** 按用户+机构归并的常购清单：勾选后一键建申领单（SQ），并可回写使用次数。 */
export default function FavoriteList() {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const profile = useAuthStore((state) => state.profile);

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [qtys, setQtys] = useState<Record<string, number>>({});
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [draft, setDraft] = useState<PurchaseDraft>(DEFAULT_DRAFT);

  const query = useQuery({
    queryKey: ['favorite', 'list'],
    queryFn: () => hq.favorite.list()
  });

  const rows = query.data ?? [];

  const remove = useMutation({
    mutationFn: (materialId: string) => hq.favorite.remove(materialId),
    onSuccess: async () => {
      message.success('已移出常购清单');
      await queryClient.invalidateQueries({ queryKey: ['favorite'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '移除失败')
  });

  const purchase = useMutation({
    mutationFn: async () => {
      const picked = rows.filter((row) => selectedKeys.includes(row.materialId));
      if (picked.length === 0) throw new Error('请先勾选要申领的物资');
      const body = {
        title: draft.title || `常购复购（${picked.length} 项）`,
        deptId: profile?.deptId ?? '',
        urgentLevel: draft.urgentLevel,
        items: picked.map((row) => ({
          materialId: row.materialId,
          materialCode: row.materialCode,
          materialName: row.materialName,
          spec: row.spec,
          unitId: row.unitId,
          unitName: row.unitName,
          qty: qtys[row.materialId] ?? row.qtyHint ?? 1,
          price: row.referencePrice
        }))
      };
      const created: ApplyRow = await hq.apply.create(body);
      // 回写使用次数（失败不影响已建单）
      await Promise.allSettled(
        picked.map((row) => hq.favorite.markUsed(row.materialId, qtys[row.materialId] ?? row.qtyHint))
      );
      return created;
    },
    onSuccess: async (created) => {
      message.success(`申领单已创建：${created.bizNo}`);
      setPurchaseOpen(false);
      setDraft(DEFAULT_DRAFT);
      setSelectedKeys([]);
      await queryClient.invalidateQueries({ queryKey: ['favorite'] });
      await queryClient.invalidateQueries({ queryKey: ['apply'] });
      navigate(`/apply/${created.id}`);
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '建单失败')
  });

  const columns: ColumnsType<FavoriteRow> = [
    { title: '物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '物资名称', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '规格', dataIndex: 'spec', width: 160, render: (value: string | null) => value || '-' },
    { title: '单位', dataIndex: 'unitName', width: 80, render: (value: string | null) => value || '-' },
    {
      title: '参考单价',
      dataIndex: 'referencePrice',
      width: 120,
      align: 'right',
      render: (value: number) => formatAmount(value, 4)
    },
    {
      title: '常用数量',
      dataIndex: 'qtyHint',
      width: 150,
      align: 'right',
      render: (_value: number, row) => (
        <InputNumber
          size="small"
          min={0}
          precision={4}
          style={{ width: '100%' }}
          value={qtys[row.materialId] ?? row.qtyHint}
          onChange={(next) =>
            setQtys((prev) => ({ ...prev, [row.materialId]: typeof next === 'number' ? next : 0 }))
          }
          aria-label={`${row.materialName ?? ''}申领数量`}
        />
      )
    },
    { title: '使用次数', dataIndex: 'useCount', width: 100, align: 'right' },
    {
      title: '操作',
      key: 'action',
      width: 110,
      fixed: 'right',
      render: (_value, row) => (
        <Button
          size="small"
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() =>
            modal.confirm({
              title: '移出常购清单？',
              content: `${row.materialName ?? ''} 将从你的常购清单移除，不影响历史申领单。`,
              okText: '移出',
              cancelText: '取消',
              onOk: () => remove.mutateAsync(row.materialId)
            })
          }
        >
          移出
        </Button>
      )
    }
  ];

  const selectedQty = rows
    .filter((row) => selectedKeys.includes(row.materialId))
    .reduce((sum, row) => sum + (qtys[row.materialId] ?? row.qtyHint ?? 0), 0);

  return (
    <Card
      className="page-card"
      title={
        <Space>
            常购清单
          <Typography.Text type="secondary">勾选后一键复购，可直接生成申领单</Typography.Text>
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<ShoppingCartOutlined />}
            disabled={selectedKeys.length === 0}
            onClick={() => setPurchaseOpen(true)}
          >
            加入申领单（{selectedKeys.length}）
          </Button>
        </Space>
      }
    >
      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="常购清单加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<FavoriteRow>
        rowKey="materialId"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: (keys) => setSelectedKeys(keys as string[])
        }}
        locale={{ emptyText: <Empty description="暂无常用物资，可在申领建单页「加入常购」" /> }}
      />

      <Modal
        title="加入申领单"
        open={purchaseOpen}
        onCancel={() => setPurchaseOpen(false)}
        okText="创建申领单"
        cancelText="取消"
        confirmLoading={purchase.isPending}
        onOk={() => purchase.mutate()}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`将按当前科室建单，共 ${selectedKeys.length} 项、合计 ${formatQty(selectedQty)}`}
          description="建单后仍需在申领详情页提交审批；物资单价取价格协议参考价，以审批通过后的实际价为准。"
        />
        <Form layout="vertical">
          <Form.Item label="申领标题">
            <Input
              placeholder="留空自动生成"
              value={draft.title}
              onChange={(event) => setDraft((prev) => ({ ...prev, title: event.target.value }))}
              maxLength={64}
            />
          </Form.Item>
          <Form.Item label="紧急程度">
            <Radio.Group
              value={draft.urgentLevel}
              onChange={(event) => setDraft((prev) => ({ ...prev, urgentLevel: event.target.value as UrgentLevel }))}
            >
              <Radio.Button value="NORMAL">普通</Radio.Button>
              <Radio.Button value="URGENT">加急</Radio.Button>
              <Radio.Button value="EMERGENCY">急救</Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Form>
        <Space size={4} wrap>
          {rows
            .filter((row) => selectedKeys.includes(row.materialId))
            .map((row) => (
              <Tag key={row.materialId}>
                {row.materialName ?? row.materialCode} × {formatQty(qtys[row.materialId] ?? row.qtyHint ?? 0)}
              </Tag>
            ))}
        </Space>
      </Modal>
    </Card>
  );
}
